import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CalendarDays, AlertCircle, Plus, Clock, CheckCircle2, CalendarCheck, ShieldCheck } from "lucide-react";
import { clsx } from "clsx";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ToastContainer } from "../../../components/ui/Toast";
import { useToast } from "../../../hooks/useToast";
import { useAuth } from "../../../hooks/useAuth";
import { useMyLeaves, useApplyLeave, useMyLeaveBalance } from "../../../hooks/useLeave";
import type { Leave, LeaveType, LeaveStatus } from "../../../lib/leaveApi";

// ── Constants ────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-xs text-[#dc2626] mt-1";

const LEAVE_TYPE_LABELS: Record<string, string> = {
  VACATION:  "Vacation Leave",
  SICK:      "Sick Leave",
  EMERGENCY: "Emergency Leave",
  MATERNITY: "Maternity Leave",
  PATERNITY: "Paternity Leave",
};

const LEAVE_TYPE_ICON: Record<string, string> = {
  VACATION:  "🏖️",
  SICK:      "🏥",
  EMERGENCY: "🚨",
  MATERNITY: "👶",
  PATERNITY: "👨‍👧",
};

const LEAVE_TYPE_COLORS: Record<string, string> = {
  VACATION:  "border-l-[#2447d9] bg-[#fafbff]",
  SICK:      "border-l-[#16a34a] bg-[#fafffe]",
  EMERGENCY: "border-l-[#dc2626] bg-[#fffafa]",
  MATERNITY: "border-l-[#7c3aed] bg-[#fdfbff]",
  PATERNITY: "border-l-[#0891b2] bg-[#f9feff]",
};

const STATUS_BADGE: Record<LeaveStatus, string> = {
  PENDING:  "bg-[#fef9c3] text-[#a16207]",
  APPROVED: "bg-[#dcfce7] text-[#15803d]",
  REJECTED: "bg-[#fee2e2] text-[#b91c1c]",
};

const STATUS_LABELS: Record<LeaveStatus, string> = {
  PENDING:  "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function countWorkingDays(start: string, end: string): number {
  if (!start || !end || end < start) return 0;
  const s = new Date(start);
  const e = new Date(end);
  let count = 0;
  const cur = new Date(s);
  while (cur <= e) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return Math.max(count, 0);
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const startStr = s.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endStr   = e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

function formatShortDate(iso: string): string {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Apply Leave Modal ─────────────────────────────────────────────────────────

interface ApplyLeaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function ApplyLeaveModal({ isOpen, onClose, onSuccess }: ApplyLeaveModalProps) {
  const applyLeave = useApplyLeave();
  const { user, activeBusinessId } = useAuth();
  const employeeName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.username;

  const { register, handleSubmit, reset, watch, setError, formState: { errors, isSubmitting } } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { type: "VACATION" },
  });

  const selectedBizId = activeBusinessId;
  const startDate  = watch("startDate");
  const endDate    = watch("endDate");
  const workingDays = countWorkingDays(startDate ?? "", endDate ?? "");

  const balanceQuery = useMyLeaveBalance(selectedBizId || null);
  const balance = balanceQuery.data?.balance ?? null;
  const remaining = balance ? balance.totalCredits - balance.usedCredits : null;
  const noCreditsLeft = balance !== null && remaining !== null && remaining <= 0;
  const exceedsCredits = balance !== null && remaining !== null && remaining > 0 && workingDays > 0 && workingDays > remaining;
  const cannotSubmit = noCreditsLeft || exceedsCredits;

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (!activeBusinessId) return;
    try {
      await applyLeave.mutateAsync({
        businessId: activeBusinessId,
        type: data.type as LeaveType,
        startDate: data.startDate,
        endDate: data.endDate,
        reason: data.reason,
        employeeName: employeeName ?? undefined,
      });
      reset();
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to submit. Please try again.";
      setError("root", { message: msg });
    }
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Apply for Leave" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

        {/* Leave balance chip */}
        {balance !== null && remaining !== null && (
          <div className={clsx(
            "flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm border",
            remaining > 0
              ? "bg-[#f0fdf4] border-[#bbf7d0] text-[#16a34a]"
              : "bg-[#fef2f2] border-[#fecaca] text-[#dc2626]",
          )}>
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span className="font-medium">
              {remaining > 0
                ? `${remaining} / ${balance.totalCredits} days remaining (${balance.year})`
                : `0 / ${balance.totalCredits} days remaining — No leave credits left`}
            </span>
          </div>
        )}

        {/* Leave type — visual pill selector */}
        <div>
          <label className={LABEL_CLASS}>Leave Type</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(LEAVE_TYPE_LABELS).map(([value, label]) => {
              const isSelected = watch("type") === value;
              return (
                <label
                  key={value}
                  className={clsx(
                    "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-medium cursor-pointer transition-colors",
                    isSelected
                      ? "border-[#2447d9] bg-[#eff3ff] text-[#2447d9]"
                      : "border-[#e2e8f0] bg-white text-[#374151] hover:border-[#c7d2fe]",
                  )}
                >
                  <input type="radio" value={value} {...register("type")} className="sr-only" />
                  <span>{LEAVE_TYPE_ICON[value]}</span>
                  <span className="truncate">{label}</span>
                </label>
              );
            })}
          </div>
          {errors.type && <p className={ERROR_CLASS}>{errors.type.message}</p>}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Start Date</label>
            <input {...register("startDate")} type="date" className={INPUT_CLASS} />
            {errors.startDate && <p className={ERROR_CLASS}>{errors.startDate.message}</p>}
          </div>
          <div>
            <label className={LABEL_CLASS}>End Date</label>
            <input {...register("endDate")} type="date" className={INPUT_CLASS} />
            {errors.endDate && <p className={ERROR_CLASS}>{errors.endDate.message}</p>}
          </div>
        </div>

        {/* Working days preview */}
        {startDate && endDate && endDate >= startDate && (
          <div className={clsx(
            "rounded-lg border px-3 py-2 text-xs",
            exceedsCredits
              ? "bg-[#fef2f2] border-[#fecaca] text-[#dc2626]"
              : "bg-[#eff3ff] border-[#c7d2fe] text-[#374151]",
          )}>
            <span className={exceedsCredits ? "text-[#dc2626]" : "text-[#64748b]"}>Working days covered: </span>
            <span className={`font-bold ${exceedsCredits ? "text-[#dc2626]" : "text-[#2447d9]"}`}>{workingDays} {workingDays === 1 ? "day" : "days"}</span>
            <span className={`ml-2 ${exceedsCredits ? "text-[#dc2626]" : "text-[#64748b]"}`}>({formatDateRange(startDate, endDate)})</span>
            {exceedsCredits && remaining !== null && (
              <p className="mt-1 font-medium">Exceeds your remaining {remaining} day{remaining !== 1 ? "s" : ""}. Reduce the leave period or ask admin to adjust your balance.</p>
            )}
          </div>
        )}

        {/* Reason */}
        <div>
          <label className={LABEL_CLASS}>Reason</label>
          <textarea
            {...register("reason")} rows={3} maxLength={500}
            className={INPUT_CLASS}
            placeholder="Briefly describe the reason for your leave..."
          />
          {errors.reason && <p className={ERROR_CLASS}>{errors.reason.message}</p>}
        </div>

        {errors.root && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">{errors.root.message}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={handleClose} disabled={isSubmitting}
            className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting || cannotSubmit}
            title={noCreditsLeft ? "You have no remaining leave credits for this year." : exceedsCredits ? "Requested days exceed your remaining leave credits." : undefined}
            className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            {isSubmitting ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Schema (kept outside component for stability) ─────────────────────────────

const schema = z.object({
  type: z.enum(["VACATION", "SICK", "EMERGENCY", "MATERNITY", "PATERNITY"]),
  startDate: z.string().min(1, "Required"),
  endDate: z.string().min(1, "Required"),
  reason: z.string().min(1, "Reason is required").max(500),
}).refine((d) => !d.startDate || !d.endDate || d.endDate >= d.startDate, {
  message: "End date must be after start date",
  path: ["endDate"],
});

// ── Leave Card ────────────────────────────────────────────────────────────────

function LeaveCard({ leave }: { leave: Leave }) {
  const colorClass = LEAVE_TYPE_COLORS[leave.type] ?? "";
  const truncatedReason = leave.reason.length > 120 ? leave.reason.slice(0, 120) + "…" : leave.reason;
  const today = todayStr();
  const isUpcoming = leave.status === "APPROVED" && leave.startDate > today;
  const isOngoing  = leave.status === "APPROVED" && leave.startDate <= today && leave.endDate >= today;

  return (
    <div className={clsx("rounded-xl border border-[#e2e8f0] border-l-4 overflow-hidden shadow-sm", colorClass)}>
      {/* Main row */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm">{LEAVE_TYPE_ICON[leave.type]}</span>
              <span className="text-sm font-semibold text-[#0f172a]">
                {LEAVE_TYPE_LABELS[leave.type] ?? leave.type}
              </span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[leave.status]}`}>
                {STATUS_LABELS[leave.status]}
              </span>
              {isOngoing && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#2447d9] px-2.5 py-0.5 text-xs font-semibold text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                  On leave now
                </span>
              )}
              {isUpcoming && (
                <span className="inline-flex items-center rounded-full bg-[#eff3ff] px-2.5 py-0.5 text-xs font-semibold text-[#2447d9]">
                  Upcoming
                </span>
              )}
            </div>
            <p className="text-xs text-[#64748b]">
              {formatDateRange(leave.startDate, leave.endDate)}
              {" · "}
              <span className="font-medium text-[#374151]">
                {leave.days} working {leave.days === 1 ? "day" : "days"}
              </span>
            </p>
            <p className="mt-1.5 text-sm text-[#374151] break-words">{truncatedReason}</p>
          </div>
        </div>
      </div>

      {/* Approved — show reviewer info */}
      {leave.status === "APPROVED" && leave.reviewedBy && (
        <div className="border-t border-[#e2e8f0] bg-[#f0fdf4] px-5 py-2.5 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-[#16a34a] shrink-0" />
          <p className="text-xs text-[#16a34a]">
            Approved by <span className="font-medium">{leave.reviewedBy}</span>
            {leave.reviewedAt && <span className="text-[#86efac]"> · {formatShortDate(leave.reviewedAt)}</span>}
          </p>
        </div>
      )}

      {/* Pending — friendly note */}
      {leave.status === "PENDING" && (
        <div className="border-t border-[#e2e8f0] bg-[#fffbeb] px-5 py-2.5 flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-[#d97706] shrink-0" />
          <p className="text-xs text-[#a16207]">Awaiting review by HR</p>
        </div>
      )}

      {/* Rejected */}
      {leave.status === "REJECTED" && (
        <div className="border-t border-[#e2e8f0] bg-[#fef2f2] px-5 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-[#dc2626] shrink-0 mt-0.5" />
            <div>
              {leave.reviewNote && (
                <p className="text-xs text-[#dc2626]">{leave.reviewNote}</p>
              )}
              {leave.reviewedBy && (
                <p className="text-[10px] text-[#f87171] mt-0.5">
                  Rejected by {leave.reviewedBy}
                  {leave.reviewedAt && ` · ${formatShortDate(leave.reviewedAt)}`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type TabValue = "ALL" | LeaveStatus;

const TABS: { label: string; value: TabValue }[] = [
  { label: "All",      value: "ALL"      },
  { label: "Pending",  value: "PENDING"  },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

export default function LeavePage() {
  const [activeTab, setActiveTab] = useState<TabValue>("PENDING");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const { toasts, toast, dismissToast } = useToast();

  const statusFilter = activeTab === "ALL" ? undefined : activeTab;
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMyLeaves(statusFilter);
  // All leaves for summary stats
  const { data: allData } = useMyLeaves(undefined);

  const filteredLeaves = data?.pages.flatMap((p) => p.items) ?? [];
  const allLeaves = allData?.pages.flatMap((p) => p.items) ?? [];

  // Summary stats
  const today = todayStr();
  const currentYear = new Date().getFullYear().toString();
  const pendingCount = allLeaves.filter((l) => l.status === "PENDING").length;
  const onLeaveNow   = allLeaves.find((l) => l.status === "APPROVED" && l.startDate <= today && l.endDate >= today);
  const nextApproved = allLeaves
    .filter((l) => l.status === "APPROVED" && l.startDate > today)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
  const daysApprovedThisYear = allLeaves
    .filter((l) => l.status === "APPROVED" && l.startDate.startsWith(currentYear))
    .reduce((s, l) => s + l.days, 0);

  const handleApplySuccess = () => {
    setShowApplyModal(false);
    toast.success("Leave application submitted successfully.");
  };

  // Grouped view for "All" tab
  const groupedLeaves = (() => {
    if (activeTab !== "ALL") return null;
    const pending  = filteredLeaves.filter((l) => l.status === "PENDING");
    const approved = filteredLeaves.filter((l) => l.status === "APPROVED");
    const rejected = filteredLeaves.filter((l) => l.status === "REJECTED");
    return { pending, approved, rejected };
  })();

  return (
    <div className="space-y-5">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Summary bar ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#c7d2fe] bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
              <CalendarDays className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0f172a]">Leave Application</h1>
              <p className="text-xs text-[#64748b]">Apply for leave and track approvals</p>
            </div>
          </div>
          <div className="shrink-0">
            <button
              onClick={() => setShowApplyModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              Apply for Leave
            </button>
            {pendingCount > 0 && (
              <p className="text-[10px] text-[#6366f1] mt-1 text-right">{pendingCount} awaiting approval</p>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-xl bg-white/70 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <Clock className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Status</p>
            </div>
            {onLeaveNow ? (
              <>
                <p className="text-sm font-bold text-[#2447d9]">On leave</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">Until {formatShortDate(onLeaveNow.endDate)}</p>
              </>
            ) : nextApproved ? (
              <>
                <p className="text-sm font-bold text-[#374151]">Upcoming</p>
                <p className="text-[10px] text-[#64748b] mt-0.5">{formatShortDate(nextApproved.startDate)}</p>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-[#94a3b8]">No upcoming</p>
                <p className="text-[10px] text-[#94a3b8] mt-0.5">No approved leave</p>
              </>
            )}
          </div>
          <div className="rounded-xl bg-white/70 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <CalendarCheck className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">This Year</p>
            </div>
            <p className={`text-sm font-bold ${daysApprovedThisYear > 0 ? "text-[#374151]" : "text-[#94a3b8]"}`}>
              {daysApprovedThisYear > 0 ? `${daysApprovedThisYear} day${daysApprovedThisYear !== 1 ? "s" : ""}` : "—"}
            </p>
            <p className="text-[10px] text-[#64748b] mt-0.5">Approved leave taken</p>
          </div>
          <div className="col-span-2 sm:col-span-1 rounded-xl bg-white/70 px-3 py-2.5">
            <div className="flex items-center gap-1 mb-1">
              <CalendarDays className="h-3 w-3 text-[#4f46e5]" />
              <p className="text-[10px] font-semibold text-[#4f46e5] uppercase tracking-wide">Total Filed</p>
            </div>
            <p className="text-sm font-bold text-[#374151]">{allLeaves.length}</p>
            <p className="text-[10px] text-[#64748b] mt-0.5">All applications</p>
          </div>
        </div>
      </div>

      {/* ── Tab filter ────────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={clsx(
              "rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
              activeTab === tab.value
                ? "bg-white text-[#0f172a] shadow-sm"
                : "text-[#64748b] hover:text-[#0f172a]",
            )}
          >
            {tab.label}
            {tab.value === "PENDING" && pendingCount > 0 && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#f59e0b] text-[10px] font-bold text-white">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : filteredLeaves.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-[#e2e8f0] bg-white py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <CalendarDays className="h-8 w-8 text-[#2447d9]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#0f172a]">
              {activeTab === "ALL" ? "No leave applications yet" : `No ${STATUS_LABELS[activeTab as LeaveStatus] ?? activeTab.toLowerCase()} leaves`}
            </h2>
            <p className="mt-1 text-sm text-[#94a3b8]">
              {activeTab === "ALL"
                ? "Tap 'Apply for Leave' above to file your first request."
                : "Switch to 'All' to see your full leave history."}
            </p>
          </div>
        </div>
      ) : groupedLeaves ? (
        <div className="space-y-5">
          {groupedLeaves.pending.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Pending</p>
              {groupedLeaves.pending.map((l) => <LeaveCard key={l.leaveId} leave={l} />)}
            </section>
          )}
          {groupedLeaves.approved.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Approved</p>
              {groupedLeaves.approved.map((l) => <LeaveCard key={l.leaveId} leave={l} />)}
            </section>
          )}
          {groupedLeaves.rejected.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Rejected</p>
              {groupedLeaves.rejected.map((l) => <LeaveCard key={l.leaveId} leave={l} />)}
            </section>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLeaves.map((l) => <LeaveCard key={l.leaveId} leave={l} />)}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}

      <ApplyLeaveModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onSuccess={handleApplySuccess}
      />
    </div>
  );
}
